import {
  DEFAULT_SCHEME,
  DEFAULT_THEME,
  SCHEMES,
  THEMES,
  type Scheme,
  type Theme,
} from '@/styles/tokens';

/**
 * Shared with the inline bootstrap script in `index.html`, which applies the
 * stored preference before first paint. Keep the key, shape, and scheme/theme
 * allowlists in sync with that script and with `src/styles/tokens.ts`.
 */
export const THEME_STORAGE_KEY = 'sb.theme';

export interface ThemePreference {
  scheme: Scheme;
  theme: Theme;
}

const isScheme = (value: unknown): value is Scheme =>
  typeof value === 'string' && (SCHEMES as readonly string[]).includes(value);

const isTheme = (value: unknown): value is Theme =>
  typeof value === 'string' && (THEMES as readonly string[]).includes(value);

export const DEFAULT_PREFERENCE: ThemePreference = {
  scheme: DEFAULT_SCHEME,
  theme: DEFAULT_THEME,
};

/**
 * Resolves the starting preference from storage, falling back to whatever the
 * document already carries and finally to the design system default. Storage can
 * throw in private browsing modes, so every access is guarded.
 */
export function readThemePreference(): ThemePreference {
  let stored: unknown;
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    stored = raw === null ? null : JSON.parse(raw);
  } catch {
    stored = null;
  }

  const fromStorage =
    typeof stored === 'object' && stored !== null ? (stored as Record<string, unknown>) : {};

  const fromDocument =
    typeof document === 'undefined'
      ? {}
      : (document.documentElement.dataset as Record<string, unknown>);

  const scheme = [fromStorage.scheme, fromDocument.scheme].find(isScheme);
  const theme = [fromStorage.theme, fromDocument.theme].find(isTheme);

  return {
    scheme: scheme ?? DEFAULT_PREFERENCE.scheme,
    theme: theme ?? DEFAULT_PREFERENCE.theme,
  };
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preference));
  } catch {
    // A failed write only costs the user their preference on the next visit.
  }
}
